import Phaser from 'phaser';
import type {
  GameRuntime,
  GameRuntimeConfig,
  GameRuntimeEvents,
  GameRuntimeState,
} from '@adgf/game-engine';

const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;
const DURATION_MS = 30_000;
const BASE_POINTS = 10;
const TARGET_RADIUS = 36;
const TARGET_COUNT = 3;
const START_SPEED = 140;
const END_SPEED = 340;
const COLOR_BG = 0x16213e;
const COLOR_TARGET = 0xe94560;
const COLOR_ACCENT = 0xf0a500;

/**
 * ComboLogic — pure rules: consecutive hits multiply points, a miss wipes
 * the combo. Kept Phaser-free so it can be unit-tested directly.
 */
export class ComboLogic {
  score = 0;
  combo = 0;
  readonly basePoints: number;

  constructor(basePoints: number = BASE_POINTS) {
    this.basePoints = basePoints;
  }

  /** Register a hit. Returns the points earned for this tap. */
  hit(): number {
    this.combo++;
    const gained = this.basePoints * this.combo;
    this.score += gained;
    return gained;
  }

  /** Register a miss / tap on empty space. */
  miss(): void {
    this.combo = 0;
  }
}

/** A moving target and its velocity (px/s). */
interface MovingTarget {
  circle: Phaser.GameObjects.Arc;
  vx: number;
  vy: number;
}

interface SceneCallbacks {
  addPoints(points: number): void;
  comboReset(): void;
  gameOver(): void;
  retry(): void;
  wantsStart(): boolean;
  onEvent(type: string, data?: Record<string, unknown>): void;
}

/**
 * ClickerScene — Phaser gameplay. Targets drift & bounce around the
 * arena; tapping one scores, tapping empty space resets the combo.
 */
class ClickerScene extends Phaser.Scene {
  private readonly cb: SceneCallbacks;
  private readonly logic = new ComboLogic();

  private timerText: Phaser.GameObjects.Text | null = null;
  private scoreText: Phaser.GameObjects.Text | null = null;
  private comboText: Phaser.GameObjects.Text | null = null;
  private targets: MovingTarget[] = [];
  private readyOverlay: Phaser.GameObjects.Container | null = null;
  private overOverlay: Phaser.GameObjects.Container | null = null;

  private remainingMs = DURATION_MS;
  private running = false;
  private created = false;

  constructor(cb: SceneCallbacks) {
    super({ key: 'ClickerScene' });
    this.cb = cb;
  }

  create(): void {
    const { width, height } = this.scale;

    this.timerText = this.add
      .text(width / 2, 36, '30', { fontSize: '40px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5);
    this.scoreText = this.add
      .text(width - 80, 36, 'Score: 0', { fontSize: '24px', color: '#e94560' })
      .setOrigin(0.5);
    this.comboText = this.add
      .text(80, 36, 'Combo x1', { fontSize: '22px', color: '#f0a500', fontStyle: 'bold' })
      .setOrigin(0.5);

    // Full-screen background: tapping it counts as a miss.
    const background = this.add.rectangle(width / 2, height / 2, width, height, COLOR_BG).setInteractive();
    background.on('pointerdown', () => this.onMiss());

    this.buildReadyOverlay(width, height);
    this.buildGameOverOverlay(width, height);

    this.created = true;
    if (this.cb.wantsStart()) this.beginRound();
  }

  requestBegin(): void {
    if (this.created) this.beginRound();
  }

  beginRound(): void {
    this.running = true;
    this.remainingMs = DURATION_MS;
    this.logic.combo = 0;
    this.logic.score = 0;
    this.hideOverlay(this.readyOverlay);
    this.hideOverlay(this.overOverlay);
    this.clearTargets();
    for (let i = 0; i < TARGET_COUNT; i++) this.spawnTarget();
    this.timerText?.setText('30');
    this.scoreText?.setText('Score: 0');
    this.comboText?.setText('Combo x1');
  }

  resetRound(): void {
    this.clearTargets();
    this.running = false;
    this.remainingMs = DURATION_MS;
  }

  setScoreDisplay(score: number): void {
    this.scoreText?.setText(`Score: ${score}`);
  }

  showFinalScore(score: number): void {
    const label = this.overOverlay?.getByName('finalScoreText') as Phaser.GameObjects.Text | null;
    label?.setText(`Final Score: ${score}`);
  }

  override update(_time: number, delta: number): void {
    if (!this.running) return;

    this.remainingMs -= delta;
    const secondsLeft = Math.max(0, Math.ceil(this.remainingMs / 1000));
    this.timerText?.setText(String(secondsLeft));
    if (this.remainingMs <= 0) {
      this.endRound();
      return;
    }

    const dt = delta / 1000;
    const speed = this.currentSpeed();
    this.targets.forEach((t) => {
      // Normalise velocity to the desired speed (in case of bounces).
      const len = Math.hypot(t.vx, t.vy) || 1;
      const nx = (t.vx / len) * speed;
      const ny = (t.vy / len) * speed;
      let x = t.circle.x + nx * dt;
      let y = t.circle.y + ny * dt;
      const r = TARGET_RADIUS;
      const { width, height } = this.scale;
      if (x < r) { x = r; t.vx = Math.abs(t.vx); }
      if (x > width - r) { x = width - r; t.vx = -Math.abs(t.vx); }
      if (y < 70 + r) { y = 70 + r; t.vy = Math.abs(t.vy); }
      if (y > height - r) { y = height - r; t.vy = -Math.abs(t.vy); }
      t.circle.setPosition(x, y);
    });
  }

  // ---- internals -------------------------------------------------------

  private currentSpeed(): number {
    const t = 1 - this.remainingMs / DURATION_MS;
    return START_SPEED + (END_SPEED - START_SPEED) * Math.min(1, Math.max(0, t));
  }

  private spawnTarget(): void {
    const { width, height } = this.scale;
    const margin = TARGET_RADIUS + 10;
    const x = Phaser.Math.Between(margin, width - margin);
    const y = Phaser.Math.Between(70 + margin, height - margin);
    const angle = Phaser.Math.FloatBetween(0, Math.PI * 2);

    const circle = this.add.circle(x, y, TARGET_RADIUS, COLOR_TARGET);
    circle.setStrokeStyle(3, 0xffffff);
    circle.setInteractive({ useHandCursor: true });
    circle.on('pointerdown', (p: Phaser.Input.Pointer) => {
      // Stop the background "miss" handler from also firing.
      p.event?.stopPropagation?.();
      this.onTargetHit(circle);
    });

    this.targets.push({
      circle,
      vx: Math.cos(angle),
      vy: Math.sin(angle),
    });
  }

  private onTargetHit(circle: Phaser.GameObjects.Arc): void {
    if (!this.running) return;
    const gained = this.logic.hit();
    this.comboText?.setText(`Combo x${this.logic.combo}`);
    this.cb.addPoints(gained);
    this.cb.onEvent('target_hit', { combo: this.logic.combo, gained });

    this.removeTarget(circle);
    this.spawnTarget();
  }

  private onMiss(): void {
    if (!this.running) return;
    this.logic.miss();
    this.comboText?.setText('Combo x1');
    this.cb.comboReset();
    this.cb.onEvent('miss', {});
  }

  private removeTarget(circle: Phaser.GameObjects.Arc): void {
    this.targets = this.targets.filter((t) => t.circle !== circle);
    circle.removeAllListeners();
    circle.destroy();
  }

  private clearTargets(): void {
    [...this.targets].forEach((t) => {
      t.circle.removeAllListeners();
      t.circle.destroy();
    });
    this.targets = [];
  }

  private endRound(): void {
    this.running = false;
    this.clearTargets();
    this.showOverlay(this.overOverlay);
    this.cb.gameOver();
  }

  private buildReadyOverlay(width: number, height: number): void {
    this.readyOverlay = this.add.container(width / 2, height / 2);
    const title = this.add.text(0, -40, 'One Tap Clicker', {
      fontSize: '36px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    const hint = this.add.text(0, 20, 'Tap moving targets. Build your combo!', {
      fontSize: '18px',
      color: '#a0a0a0',
    });
    hint.setOrigin(0.5);
    const btn = this.add.rectangle(0, 90, 220, 64, COLOR_ACCENT);
    const btnText = this.add.text(0, 90, 'START', {
      fontSize: '24px',
      color: '#16213e',
      fontStyle: 'bold',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.cb.wantsStart() || this.requestBegin());
    this.readyOverlay.add([title, hint, btn, btnText]);
  }

  private buildGameOverOverlay(width: number, height: number): void {
    this.overOverlay = this.add.container(width / 2, height / 2);
    const title = this.add.text(0, -60, "Time's Up!", {
      fontSize: '40px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    const score = this.add.text(0, -10, 'Final Score: 0', {
      fontSize: '24px',
      color: '#e94560',
    });
    score.setOrigin(0.5);
    score.setName('finalScoreText');
    const btn = this.add.rectangle(0, 70, 240, 64, COLOR_ACCENT);
    const btnText = this.add.text(0, 70, 'Play Again', {
      fontSize: '24px',
      color: '#16213e',
      fontStyle: 'bold',
    });
    btnText.setOrigin(0.5);
    btn.setInteractive({ useHandCursor: true });
    btn.on('pointerdown', () => this.cb.retry());
    this.overOverlay.add([title, score, btn, btnText]);
    this.overOverlay.setVisible(false);
  }

  private showOverlay(overlay: Phaser.GameObjects.Container | null): void {
    overlay?.setVisible(true);
    overlay?.setActive(true);
  }

  private hideOverlay(overlay: Phaser.GameObjects.Container | null): void {
    overlay?.setVisible(false);
    overlay?.setActive(false);
  }
}

/**
 * ClickerGame — GameRuntime wrapper around the Phaser game. Owns the
 * Phaser.Game instance, the score and the runtime state machine.
 */
export class ClickerGame implements GameRuntime {
  private readonly config: GameRuntimeConfig;
  private readonly events: GameRuntimeEvents;
  private state: GameRuntimeState = 'idle';
  private score = 0;
  private wantStart = false;
  private game: Phaser.Game | null = null;
  private scene: ClickerScene | null = null;

  constructor(config: GameRuntimeConfig, events: GameRuntimeEvents = {}) {
    this.config = config;
    this.events = events;
  }

  mount(): void {
    if (this.state !== 'idle') return;

    this.scene = new ClickerScene(this.buildCallbacks());

    this.game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: this.config.parent,
      width: this.config.width ?? GAME_WIDTH,
      height: this.config.height ?? GAME_HEIGHT,
      backgroundColor: COLOR_BG,
      scale: {
        mode: Phaser.Scale.FIT,
        width: this.config.width ?? GAME_WIDTH,
        height: this.config.height ?? GAME_HEIGHT,
      },
      scene: [this.scene],
    });

    this.state = 'mounted';
  }

  start(): void {
    this.wantStart = true;
    if (
      this.state === 'mounted' ||
      this.state === 'paused' ||
      this.state === 'gameover'
    ) {
      this.state = 'running';
      this.events.onStart?.();
      this.scene?.requestBegin();
    }
  }

  pause(): void {
    if (this.state !== 'running') return;
    this.state = 'paused';
    this.scene?.scene?.pause();
    this.events.onPause?.();
  }

  resume(): void {
    if (this.state !== 'paused') return;
    this.state = 'running';
    this.scene?.scene?.resume();
    this.events.onResume?.();
  }

  destroy(): void {
    if (this.state === 'destroyed') return;
    this.game?.destroy(true);
    this.game = null;
    this.scene = null;
    this.state = 'destroyed';
  }

  getScore(): number {
    return this.score;
  }

  getState(): GameRuntimeState {
    return this.state;
  }

  retry(): void {
    this.score = 0;
    this.events.onScoreChange?.(this.score);
    this.scene?.resetRound();
    this.scene?.setScoreDisplay(this.score);
    this.state = 'running';
    this.events.onRetry?.();
    this.wantStart = true;
    this.scene?.requestBegin();
  }

  private buildCallbacks(): SceneCallbacks {
    return {
      addPoints: (points: number) => {
        this.score += points;
        this.events.onScoreChange?.(this.score);
        this.scene?.setScoreDisplay(this.score);
      },
      comboReset: () => {
        this.events.onEvent?.('combo_reset', {});
      },
      gameOver: () => {
        this.state = 'gameover';
        this.scene?.showFinalScore(this.score);
        this.events.onGameOver?.(this.score, { duration: DURATION_MS / 1000 });
      },
      retry: () => this.retry(),
      wantsStart: () => this.wantStart,
      onEvent: (type: string, data?: Record<string, unknown>) => {
        this.events.onEvent?.(type, data);
      },
    };
  }
}
